
export enum Severity {
  NONE = 'NONE',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

export enum InspectionMode {
  CRACK_ONLY = 'CRACK_ONLY',
  GENERAL_NDT = 'GENERAL_NDT',
  FOULING_INSPECTION = 'FOULING_INSPECTION',
  WELD_INTEGRITY = 'WELD_INTEGRITY'
}

export enum ImageResolutionMode {
  STANDARD = 'STANDARD',           // 1600px — default, fast & cost-efficient
  HIGH_RES = 'HIGH_RES',           // 2560px — recommended balance of detail vs cost
  ULTRA_4K = 'ULTRA_4K',           // 3840px — maximum detail, highest cost/latency
}

export const IMAGE_RESOLUTION_OPTIONS: { mode: ImageResolutionMode; label: string; px: number; desc: string }[] = [
  { mode: ImageResolutionMode.STANDARD, label: 'Standard Detection', px: 1600, desc: 'Fast · Cost-efficient · 1600px' },
  { mode: ImageResolutionMode.HIGH_RES, label: 'High Resolution Detection', px: 2560, desc: 'Recommended · Better crack detail · 2560px' },
  { mode: ImageResolutionMode.ULTRA_4K, label: 'Ultra 4K Detection', px: 3840, desc: 'Maximum detail · High cost & latency · 4K' },
];

export interface TelemetryPoint {
  timestamp: number;
  x: number;
  y: number;
  z: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  quality: number;
  tilt?: number; // Camera tilt in degrees (-90 to 90)
  gpsTime?: number;
  scalar?: number; // Normalized intensity or field value (0-1)
  forceCapture?: boolean; // Guaranteed trigger for visual mapping
  lookInward?: boolean; // Camera is facing the center of the asset (opposite wall)
  isTransit?: boolean; // Trajectory is for entry/exit, not inspection
}

export interface SystemHealthPoint {
  bootTime: number;
  cpuLoad: number;
  gpuLoad: number;
  cpuTemp: number;
  gpuTemp: number;
  powerDraw: number;
}

export interface Finding {
  type: string;
  severity: Severity;
  description: string;
  recommendation: string;
  prevention: string;
  spatialAnchoring?: string;
  box: [number, number, number, number];
  spatialCoords?: { x: number, y: number, z: number };
  rotation?: { x: number, y: number, z: number, w: number };
  azimuth?: number; // Degrees relative to asset north/start
  frameImage?: string; // Snapshot of the finding
  isVerified?: boolean; // Human acceptance flag
  labNumber?: number; // Sequential number as shown in Laboratory UI (1, 2, 3...)
  labId?: string; // Full lab ID e.g. EV_V6.0_1
  // Technical Metadata for Mapping
  timestamp?: number;
  sourceAnalysis?: CrackAnalysis;
}

export interface ProjectMetadata {
  customer?: string;
  asset?: string;
  pilot?: string;
}

export interface Project {
  id: string;
  projectNumber: string;
  createdAt: number;
  metadata?: ProjectMetadata;
}

export interface CrackAnalysis {
  detected: boolean;
  severity: Severity;
  mode: InspectionMode;
  type?: string;
  location?: string;
  description: string;
  recommendation: string;
  confidenceScore: number;
  timestamp?: number;
  findings?: Finding[];
  frameImage?: string;
  videoName?: string;
  analysisDuration?: number;
  flightDuration?: number;
  evaluationSpeed?: number;
  diagnosticNote: string;
}

export interface InspectionRecord {
  id: string;
  projectId: string;
  timestamp: number;
  imageUrl: string;
  analysis: CrackAnalysis;
  timeline?: CrackAnalysis[];
  telemetry?: TelemetryPoint[];
  systemHealth?: SystemHealthPoint[];
  videoName?: string;
  videoFps?: number;
  analysisDuration?: number;
  flightDuration?: number;
  lasModelName?: string;
  modelPoints?: TelemetryPoint[];
}

export interface TrainingExample {
  image?: string;                // Optional now — NAS-served entries have an imageUrl instead
  imageUrl?: string;             // URL to the .jpg on the NAS (e.g. "./vault_images/verified_xxx_MSD.jpg")
  timestamp: number;
  analysis: CrackAnalysis;
  timeline?: CrackAnalysis[];
  // Backwards compatibility
  findings?: Finding[];
  description?: string;
  severity?: Severity;
  isVerified?: boolean;
  // Per-defect split fields (one entry per finding for AI training)
  sourceGroup?: number;          // Original entry timestamp — splits from one image share this
  splitVersion?: number;         // Marker so migration doesn't re-split already-split entries (current: 1)
  // Traceability
  lastVerifiedBy?: string;       // Persistent browser/machine ID of the user who last verified this entry
  lastVerifiedAt?: number;       // Unix ms timestamp of the last verification
  // Filename on the NAS (stable reference, survives if imageUrl base path changes)
  remoteFile?: string;           // e.g. "verified_1776850046802_MSD.jpg"
  dupIndex?: number;             // Disambiguation suffix when same-type findings coexist in a sourceGroup
}

export type ViewState = 'HOME' | 'LIVE_SCAN' | 'VIDEO_INSPECTION_T' | 'VIDEO_INSPECTION_M' | 'VIDEO_INSPECTION_S' | 'IMAGE_INSPECTION' | 'RESULTS' | 'TRAINING' | 'TELEMETRY' | 'MISSION_PLANNER';

export interface LidarCalibration {
  sn: string;
  prodLine: string;
  buildDate: string;
  beamCount: number;
  verticalFov: [number, number];
  status: string;
}

export interface MissionManifest {
  id: string;
  identifier: string;
  files: {
    flightLog?: string;
    lidarMetadata?: string;
    rgbVideos: string[];
    thermalVideo?: string;
    syslogs?: string;
  };
  robotComponents: {
    component: string;
    model: string;
    serial: string;
    firmware: string;
  }[];
}
export const GEA_DEFECT_REGISTRY = [
  { title: "Mechanical scratching deformation ( MSD)", type: "MSD", desc: "Surface deformation or material displacement caused by intense mechanical contact or scratching.", icon: "⚡", color: "text-red-400", hex: "#f87171" },
  { title: "Through-Wall Crack", type: "Crack", desc: "Full thickness separation of material, often following a jagged path.", icon: "💔", color: "text-red-600", hex: "#dc2626" },
  { title: "Polished Weld Fatigue / HAZ Crack", type: "Weld Defect", desc: "Cracks forming at polished weld junctions or within the HAZ.", icon: "🛡️", color: "text-emerald-400", hex: "#34d399" },
  { title: "Dents / Impact Damage", type: "Dent", desc: "Surface depressions caused by mechanical impact or pressure.", icon: "🔨", color: "text-blue-400", hex: "#60a5fa" },
  { title: "Burned Product / Carbonization Residue", type: "Burned Residue", desc: "Caramelized organic matter or carbonized residues found in spray dryers.", icon: "🔥", color: "text-amber-600", hex: "#d97706" },
  { title: "Residue / Scaled Build-up", type: "Residue", desc: "Organic or chemical residue adhering to the surface.", icon: "🏷️", color: "text-amber-500", hex: "#f59e0b" },
  { title: "Milk Stone / Calcium Scaling", type: "Fouling", desc: "White-to-yellowish mineral and protein deposits.", icon: "🥛", color: "text-slate-100", hex: "#f1f5f9" },
  { title: "Spray Dryer Powder Accumulation", type: "Product Buildup", desc: "Large clumps of dry product adhering to chamber walls.", icon: "💨", color: "text-orange-200", hex: "#fed7aa" },
  { title: "Older Repairs / Patches", type: "Repair Site", desc: "Sites of previous maintenance, plates or grind marks.", icon: "🛡️", color: "text-sky-400", hex: "#38bdf8" },
  { title: "Pitting Corrosion", type: "Pitting", desc: "Small, deep holes localized on the surface.", icon: "🕕", color: "text-orange-400", hex: "#fb923c" },
  { title: "Mechanical Scratches / Drag Marks", type: "Scratch", desc: "Surface marks from handling or abrasive cleaning.", icon: "📏", color: "text-slate-800", hex: "#1e293b" },
  { title: "Unknown Anomaly - Close-up Roping Check needed", type: "Unknown Anomaly", desc: "Significant surface disruption that cannot be identified. Requires manual roping inspection.", icon: "❓", color: "text-purple-400", hex: "#c084fc" }
];


